import type { App } from "vue";

import actions from "~/actions";
import * as properties from "~/common/properties";
import DocInfoSummary from "~/components/DocInfoSummary.vue";
import ChatPreviewTooltip from "~/components/chat/ChatPreviewTooltip.vue";
import DocPreview from "~/components/docs/DocPreview.vue";
import TaskDropdown from "~/components/docs/TaskDropdown.vue";
import Avatar from "~/components/dumb/Avatar.vue";
import CheckboxChip from "~/components/dumb/CheckboxChip.vue";
import CheckboxEditor from "~/components/dumb/CheckboxEditor.vue";
import DartboardEditor from "~/components/dumb/DartboardEditor.vue";
import DatePicker from "~/components/dumb/DatePicker.vue";
import DatesChip from "~/components/dumb/DatesChip.vue";
import DatesEditor from "~/components/dumb/DatesEditor.vue";
import DropdownDeleteMenuItem from "~/components/dumb/DropdownDeleteMenuItem.vue";
import EntityPreviewTooltip from "~/components/dumb/EntityPreviewTooltip.vue";
import ExportDropdown from "~/components/dumb/ExportDropdown.vue";
import FormAttachmentEditor from "~/components/dumb/FormAttachmentEditor.vue";
import FormNumberInput from "~/components/dumb/FormNumberInput.vue";
import FormTextInput from "~/components/dumb/FormTextInput.vue";
import GenericSubdropdown from "~/components/dumb/GenericSubdropdown.vue";
import LinkToRelationshipsDropdown from "~/components/dumb/LinkToRelationshipsDropdown.vue";
import MultiselectChip from "~/components/dumb/MultiselectChip.vue";
import NumberChip from "~/components/dumb/NumberChip.vue";
import NumberEditor from "~/components/dumb/NumberEditor.vue";
import PageIcon from "~/components/dumb/PageIcon.vue";
import PriorityChip from "~/components/dumb/PriorityChip.vue";
import PriorityEditor from "~/components/dumb/PriorityEditor.vue";
import RecurrenceEditor from "~/components/dumb/RecurrenceEditor.vue";
import SelectEditor from "~/components/dumb/SelectEditor.vue";
import SizeEditor from "~/components/dumb/SizeEditor.vue";
import SizeChip from "~/components/dumb/SizeChip.vue";
import SkillCard from "~/components/dumb/SkillCard.vue";
import StatusChip from "~/components/dumb/StatusChip.vue";
import StatusEditor from "~/components/dumb/StatusEditor.vue";
import StatusIcon from "~/components/dumb/StatusIcon.vue";
import TaskCard from "~/components/dumb/TaskCard.vue";
import TaskKindEditor from "~/components/dumb/TaskKindEditor.vue";
import TaskUpdateDropdown from "~/components/dumb/TaskUpdateDropdown.vue";
import TextChip from "~/components/dumb/TextChip.vue";
import TextEditor from "~/components/dumb/TextEditor.vue";
import UserChip from "~/components/dumb/UserChip.vue";
import UserCard from "~/components/dumb/UserCard.vue";
import UserEditor from "~/components/dumb/UserEditor.vue";
import OptionEditDropdown from "~/components/options/OptionEditDropdown.vue";
import OptionsEditor from "~/components/options/OptionsEditor.vue";
import FormEditor from "~/components/text/FormEditor.vue";
import * as transformers from "~/components/text/transformers";
import { FULL_DART_EDITOR_NODES } from "~/components/text/utils";
import TimeTrackingEditor from "~/components/timeTracking/TimeTrackingEditor.vue";
import CheckboxCellEditor from "~/components/visualization/list/cellEditors/CheckboxCellEditor.vue";
import DartboardCellEditor from "~/components/visualization/list/cellEditors/DartboardCellEditor.vue";
import DatesCellEditor from "~/components/visualization/list/cellEditors/DatesCellEditor.vue";
import DuidCellEditor from "~/components/visualization/list/cellEditors/DuidCellEditor.vue";
import NumberCellEditor from "~/components/visualization/list/cellEditors/NumberCellEditor.vue";
import OptionCellEditor from "~/components/visualization/list/cellEditors/OptionCellEditor.vue";
import OrderCellEditor from "~/components/visualization/list/cellEditors/OrderCellEditor.vue";
import PriorityCellEditor from "~/components/visualization/list/cellEditors/PriorityCellEditor.vue";
import SelectCellEditor from "~/components/visualization/list/cellEditors/SelectCellEditor.vue";
import SizeCellEditor from "~/components/visualization/list/cellEditors/SizeCellEditor.vue";
import StatusCellEditor from "~/components/visualization/list/cellEditors/StatusCellEditor.vue";
import TaskKindCellEditor from "~/components/visualization/list/cellEditors/TaskKindCellEditor.vue";
import TextCellEditor from "~/components/visualization/list/cellEditors/TextCellEditor.vue";
import TimeTrackingCellEditor from "~/components/visualization/list/cellEditors/TimeTrackingCellEditor.vue";
import TitleCellEditor from "~/components/visualization/list/cellEditors/TitleCellEditor.vue";
import UserCellEditor from "~/components/visualization/list/cellEditors/UserCellEditor.vue";
import ConfigHeaderRenderer from "~/components/visualization/list/headerRenderers/ConfigHeaderRenderer.vue";
import NewTaskHeaderRenderer from "~/components/visualization/list/headerRenderers/NewTaskHeaderRenderer.vue";
import StandardHeaderRenderer from "~/components/visualization/list/headerRenderers/StandardHeaderRenderer.vue";
import { initCommands } from "~/constants/command";
import {
  CONFIG_HEADER_RENDERER,
  DARTBOARD_CELL_EDITOR,
  DOC_PREVIEW,
  DROPDOWN_PARENT_ON_AFTER_CLOSE_FUNCTION,
  DROPDOWN_PARENT_ON_AFTER_OPEN_FUNCTION,
  DUID_CELL_EDITOR,
  NEW_TASK_HEADER_RENDERER,
  ORDER_CELL_EDITOR,
  PRIORITY_CELL_EDITOR,
  RECURRENCE_EDITOR,
  SELECT_CELL_EDITOR,
  SIZE_CELL_EDITOR,
  SKILL_CARD,
  STANDARD_HEADER_RENDERER,
  TASK_CARD,
  TASK_KIND_CELL_EDITOR,
  TIME_TRACKING_CELL_EDITOR,
  TITLE_CELL_EDITOR,
  USER_CARD,
} from "~/constants/injection";
import { useDataStore } from "~/stores";
import AdtlDates from "~/views/settings/PropertiesView/AdtlDates.vue";
import AdtlKind from "~/views/settings/PropertiesView/AdtlKind.vue";
import AdtlNumber from "~/views/settings/PropertiesView/AdtlNumber.vue";
import AdtlSelect from "~/views/settings/PropertiesView/AdtlSelect.vue";
import AdtlSize from "~/views/settings/PropertiesView/AdtlSize.vue";
import AdtlStatus from "~/views/settings/PropertiesView/AdtlStatus.vue";
import AdtlUser from "~/views/settings/PropertiesView/AdtlUser.vue";

export const provideComponents = (app: App) => {
  app.provide(CONFIG_HEADER_RENDERER, ConfigHeaderRenderer);
  app.provide(DARTBOARD_CELL_EDITOR, DartboardCellEditor);
  app.provide(DUID_CELL_EDITOR, DuidCellEditor);
  app.provide(NEW_TASK_HEADER_RENDERER, NewTaskHeaderRenderer);
  app.provide(ORDER_CELL_EDITOR, OrderCellEditor);
  app.provide(PRIORITY_CELL_EDITOR, PriorityCellEditor);
  app.provide(SELECT_CELL_EDITOR, SelectCellEditor);
  app.provide(SIZE_CELL_EDITOR, SizeCellEditor);
  app.provide(STANDARD_HEADER_RENDERER, StandardHeaderRenderer);
  app.provide(TASK_KIND_CELL_EDITOR, TaskKindCellEditor);
  app.provide(TITLE_CELL_EDITOR, TitleCellEditor);
  app.provide(TIME_TRACKING_CELL_EDITOR, TimeTrackingCellEditor);

  app.provide(DOC_PREVIEW, DocPreview);
  app.provide(RECURRENCE_EDITOR, RecurrenceEditor);
  app.provide(SKILL_CARD, SkillCard);
  app.provide(TASK_CARD, TaskCard);
  app.provide(USER_CARD, UserCard);

  app.provide(DROPDOWN_PARENT_ON_AFTER_OPEN_FUNCTION, () => undefined);
  app.provide(DROPDOWN_PARENT_ON_AFTER_CLOSE_FUNCTION, () => undefined);

  initCommands(actions, Avatar, ChatPreviewTooltip, EntityPreviewTooltip, PageIcon, StatusIcon);

  actions.context.init({
    DatePicker,
    DocInfoSummary,
    DropdownDeleteMenuItem,
    ExportDropdown,
    GenericSubdropdown,
    LinkToRelationshipsDropdown,
    OptionEditDropdown,
    TaskDropdown,
    TaskUpdateDropdown,
  });

  transformers.init(FULL_DART_EDITOR_NODES);

  properties.init(
    useDataStore(),
    AdtlDates,
    AdtlKind,
    AdtlNumber,
    AdtlSelect,
    AdtlSize,
    AdtlStatus,
    AdtlUser,
    Avatar,
    CheckboxCellEditor,
    CheckboxChip,
    CheckboxEditor,
    DartboardEditor,
    DatesCellEditor,
    DatesChip,
    DatesEditor,
    FormAttachmentEditor,
    FormEditor,
    MultiselectChip,
    NumberCellEditor,
    NumberChip,
    NumberEditor,
    OptionCellEditor,
    OptionsEditor,
    PageIcon,
    PriorityCellEditor,
    PriorityChip,
    PriorityEditor,
    SelectCellEditor,
    SelectEditor,
    SizeCellEditor,
    SizeEditor,
    StatusCellEditor,
    StatusChip,
    StatusEditor,
    StatusIcon,
    TaskKindEditor,
    TaskKindCellEditor,
    TextCellEditor,
    TextChip,
    TextEditor,
    TimeTrackingEditor,
    TimeTrackingCellEditor,
    UserCellEditor,
    UserChip,
    UserEditor,
    FormNumberInput,
    FormTextInput,
    SizeChip,
    transformers.convertMarkdownToHtml,
    transformers.convertToMarkdown
  );
};
